import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import axios from 'axios';
import '../styles/Notes.css';

const COLOR_PRESETS = [
  '#22dddd', '#7eb8f7', '#c77dff', '#ff6b6b',
  '#ffd93d', '#6bcb77', '#ff9a3c', '#e8e8e8',
  '#aaaaaa', '#ffffff',
];

// Register inline HTML tags that Toast UI's default convertors don't handle.
// Without this, <font>/<u>/<mark>/<kbd>/<sub>/<sup>/<span> are silently dropped
// during markdown→WYSIWYG conversion (only b/i/s/code/a/img/hr/br/pre are
// recognized by default — see toastui-editor/dist/esm/index.js line 20509).
// Registering keys here triggers schema mark creation; toDOM renders the real
// tag with original htmlAttrs preserved.
const INLINE_HTML_TAGS = ['font', 'u', 'mark', 'kbd', 'sub', 'sup', 'span'];
const passthroughHtmlInline = (node, ctx) => ({
  type: ctx.entering ? 'openTag' : 'closeTag',
  tagName: node.type,
  attributes: node.attrs || {},
});
const customHTMLRenderer = {
  htmlInline: INLINE_HTML_TAGS.reduce((acc, tag) => {
    acc[tag] = passthroughHtmlInline;
    return acc;
  }, {}),
};

function ColorPicker({ pos, onSelect, onClose }) {
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.notes-color-picker')) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div className="notes-color-picker" style={{
      left: Math.min(pos.x, window.innerWidth - 175),
      top: Math.min(pos.y, window.innerHeight - 80),
    }}>
      {COLOR_PRESETS.map(color => (
        <div
          key={color}
          className="notes-color-swatch"
          style={{ background: color }}
          onMouseDown={(e) => { e.preventDefault(); onSelect(color); }}
          title={color}
        />
      ))}
    </div>
  );
}

// token prop: JWT token (already authenticated by parent)
// 跟随全站主题：App.js 把当前主题写在 <html data-theme> 上。
// 以前这里写死 theme="dark"，浅色模式下笔记区还是黑的。
function useSiteTheme() {
  const read = () => document.documentElement.getAttribute('data-theme') || 'dark';
  const [theme, setTheme] = useState(read);
  useEffect(() => {
    const ob = new MutationObserver(() => setTheme(read()));
    ob.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => ob.disconnect();
  }, []);
  return theme;
}

export default function NotesEditor({ token }) {
  const [content, setContent] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [isSource, setIsSource] = useState(() => window.innerWidth < 768);
  const [colorPickerPos, setColorPickerPos] = useState(null);
  const siteTheme = useSiteTheme();
  const editorRef = useRef(null);
  const saveTimer = useRef(null);
  const savedRangeRef = useRef(null);
  const textareaRef = useRef(null);
  // Markdown captured on Editor's first onChange after mount. Toast UI fires
  // onChange when it parses initialValue too, so we use that fire to record a
  // baseline and skip the spurious "save". Subsequent fires save only when
  // the new markdown differs from this baseline. Reset on Editor remount.
  const baselineMdRef = useRef(null);

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) return;
    axios.get('/api/notes', authHeaders())
      .then(res => {
        const c = res.data.content || '';
        setContent(c);
        setSourceText(c);
        setLoaded(true);
        // scroll to bottom after paint (textarea or WYSIWYG)
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
          } else {
            // WYSIWYG mode: scroll the editor wrapper
            const el = editorRef.current?.getInstance()?.getEditorElements()?.wwEditor;
            if (el) el.scrollTop = el.scrollHeight;
          }
        }, 200);
      })
      .catch(() => {});
  }, [token, authHeaders]);

  // Inline HTML is rendered natively now: customHTMLRenderer registers font/u/
  // mark/kbd/sub/sup/span as ProseMirror marks, so they round-trip as real DOM
  // elements with their original attributes. Browsers handle <font color>,
  // <span style>, <sub>, <sup>, etc. natively. No JS post-processing needed —
  // mutating ProseMirror-managed DOM externally caused a re-render feedback
  // loop that froze the editor on click.

  const scheduleSave = useCallback((md) => {
    setSaveStatus('saving');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put('/api/notes', { content: md }, authHeaders());
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch {
        setSaveStatus('error');
      }
    }, 1500);
  }, [authHeaders]);

  const [sourceText, setSourceText] = useState('');

  // WYSIWYG onChange fires on initial parse AND on user edits. Capture the
  // first fire as a baseline (Toast UI's normalized markdown for the loaded
  // content) and only save when subsequent fires differ from it. This avoids
  // the round-trip corruption that previously duplicated lines on every load.
  // Inline HTML round-trips correctly via customHTMLRenderer's htmlInline
  // marks, so no merge is needed to preserve <font>/<u>/<mark>/etc.
  const handleChange = useCallback(() => {
    const md = editorRef.current?.getInstance().getMarkdown();
    if (md == null) return;
    if (baselineMdRef.current === null) {
      baselineMdRef.current = md;
      return;
    }
    if (md === baselineMdRef.current) return;
    baselineMdRef.current = md;
    setSourceText(md);
    scheduleSave(md);
  }, [scheduleSave]);

  // 切主题会让 Editor 带着新 key 重挂载，和 toggleMode 一样要先清基线，
  // 否则重挂后的第一次 onChange 会被当成真实编辑，触发一次没必要的保存。
  useEffect(() => { baselineMdRef.current = null; }, [siteTheme]);

  const toggleMode = useCallback(() => {
    if (isSource) {
      // source → preview: Editor remounts; reset baseline so its first
      // onChange (initial parse of sourceText) is treated as the new baseline
      baselineMdRef.current = null;
      setIsSource(false);
    } else {
      // preview → source: Editor unmounts, textarea shows sourceText
      setIsSource(true);
      requestAnimationFrame(() => {
        if (textareaRef.current) textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      });
    }
  }, [isSource]);

  const handleSourceChange = useCallback((e) => {
    if (!loaded) return;
    const val = e.target.value;
    setSourceText(val);
    scheduleSave(val);
  }, [loaded, scheduleSave]);

  const applyColor = useCallback((color) => {
    const saved = savedRangeRef.current;
    if (!saved) return;
    const colored = `<font color="${color}" style="color:${color}">${saved.text}</font>`;

    if (saved.type === 'source') {
      // textarea: direct string splice at saved selection positions
      const newText = saved.fullText.slice(0, saved.start) + colored + saved.fullText.slice(saved.end);
      setSourceText(newText);
      scheduleSave(newText);
    } else {
      // wysiwyg: use the markdown snapshot saved at keypress time
      const editor = editorRef.current?.getInstance();
      if (!editor) return;
      const md = saved.md;
      const idx = md.indexOf(saved.text);
      if (idx >= 0) {
        const newMd = md.slice(0, idx) + colored + md.slice(idx + saved.text.length);
        // setMarkdown will fire onChange — treat that as a real edit
        // (different from baseline) so it saves through normally
        editor.setMarkdown(newMd);
      }
    }
    setColorPickerPos(null);
    savedRangeRef.current = null;
  }, [scheduleSave]);

  const closeColorPicker = useCallback(() => {
    setColorPickerPos(null);
    savedRangeRef.current = null;
  }, []);

  const statusLabel = saveStatus === 'saving' ? '保存中…'
    : saveStatus === 'saved' ? '已保存'
    : saveStatus === 'error' ? '保存失败'
    : '';

  if (content === null) return <div className="notes-loading">加载中…</div>;

  return (
    <div className="notes-editor-wrap">
      <div className="notes-toolbar">
        <span className="notes-title">Notes · {isSource ? '源码' : '编辑'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span className={`notes-save-status ${saveStatus}`}>{statusLabel}</span>
          <button className="notes-export-btn" title="复制源码" onClick={() => {
            navigator.clipboard.writeText(sourceText).catch(() => {});
          }}>⎘</button>
          <button className="notes-export-btn" title="导出 Markdown" onClick={() => {
            const blob = new Blob([sourceText], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `notes-${new Date().toISOString().slice(0,10)}.md`;
            a.click();
            URL.revokeObjectURL(url);
          }}>↓</button>
        </div>
      </div>

      <div className={`notes-body notes-toastui${isSource ? ' is-source' : ''}`}>
        {loaded && isSource && (
          <textarea
            ref={textareaRef}
            className="notes-source-textarea"
            value={sourceText}
            onChange={handleSourceChange}
            spellCheck={false}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyE') {
                e.preventDefault();
                const el = e.target;
                const start = el.selectionStart;
                const end = el.selectionEnd;
                const text = sourceText.slice(start, end);
                if (!text) return;
                savedRangeRef.current = { type: 'source', start, end, text, fullText: sourceText };
                const rect = el.getBoundingClientRect();
                setColorPickerPos({ x: rect.left + 40, y: rect.top + 40 });
              }
            }}
          />
        )}
        {!isSource && loaded && (
          <Editor
            key={siteTheme}
            ref={editorRef}
            initialValue={sourceText}
            initialEditType="wysiwyg"
            previewStyle="vertical"
            height="100%"
            theme={siteTheme === 'light' ? 'light' : 'dark'}
            hideModeSwitch={true}
            toolbarItems={[]}
            onChange={handleChange}
            usageStatistics={false}
            customHTMLSanitizer={(html) => html}
            customHTMLRenderer={customHTMLRenderer}
            onKeydown={(mode, e) => {
              if (mode !== 'wysiwyg') return;
              const editor = editorRef.current?.getInstance();
              if (!editor) return;
              const ctrl = e.ctrlKey || e.metaKey;
              const shift = e.shiftKey;

              if (ctrl) {
                if (shift && e.code === 'KeyK') {
                  e.preventDefault(); editor.exec('codeBlock');
                } else if (!shift && e.key === ']') {
                  e.preventDefault(); editor.exec('bulletList');
                } else if (!shift && e.key === '[') {
                  e.preventDefault(); editor.exec('orderedList');
                } else if (shift && e.code === 'KeyQ') {
                  e.preventDefault(); editor.exec('blockQuote');
                } else if (shift && e.code === 'KeyM') {
                  e.preventDefault(); editor.insertText('$$\n\n$$');
                } else if (shift && e.code === 'KeyE') {
                  e.preventDefault();
                  const view = editor.wwEditor?.view;
                  if (view) {
                    const { from, to } = view.state.selection;
                    const text = from < to ? view.state.doc.textBetween(from, to) : '';
                    if (text) {
                      savedRangeRef.current = { type: 'wysiwyg', text, md: editor.getMarkdown() };
                      const sel = window.getSelection();
                      const rect = sel?.rangeCount > 0
                        ? sel.getRangeAt(0).getBoundingClientRect()
                        : { left: 100, bottom: 100 };
                      setColorPickerPos({ x: rect.left, y: rect.bottom + 8 });
                    }
                  }
                }
                return;
              }

              if (e.key === '>') {
                e.preventDefault();
                editor.insertText('>');
                return;
              }

              if (e.key === ' ') {
                const sel = window.getSelection();
                if (sel?.isCollapsed && sel.rangeCount > 0) {
                  const range = sel.getRangeAt(0);
                  const node = range.startContainer;
                  if (node.nodeType === Node.TEXT_NODE &&
                      node.textContent.slice(0, range.startOffset) === '>') {
                    e.preventDefault();
                    let deleted = false;
                    try {
                      const view = editor.wwEditor?.view;
                      if (view) {
                        const { state, dispatch } = view;
                        const { from } = state.selection;
                        dispatch(state.tr.delete(from - 1, from));
                        deleted = true;
                      }
                    } catch {}
                    if (deleted) editor.exec('blockQuote');
                    else editor.insertText(' ');
                  }
                }
              }
            }}
          />
        )}
      </div>

      {colorPickerPos && (
        <ColorPicker
          pos={colorPickerPos}
          onSelect={applyColor}
          onClose={closeColorPicker}
        />
      )}

      <button
        className={`notes-mode-btn${isSource ? ' source' : ''}`}
        onClick={toggleMode}
        title={isSource ? '切换到编辑' : '切换到源码'}
      >
        {isSource ? '◎' : '⌨'}
      </button>
    </div>
  );
}
